import React, { useEffect, useState, useContext } from "react";
import { Col, Row, Table, Button, InputGroup, Form } from "react-bootstrap";
import { FiSearch, FiPackage, FiClock, FiCheckCircle, FiCheck, FiX, FiEye, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "../../../assets/css/admin/screens/product/AdminProductManagement.css";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import axios from "axios";
import MyContext from "../../../contexts/AppContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductManagement = () => {
  const [isDark, setIsDark] = useState(false);
  const { baseUrl, baseUrlForFiles, adminAuthToken } = useContext(MyContext);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // Pagination (backend se aata hai)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // View Product modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const viewAllProduct = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/product/viewAllProduct`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        params: { page: currentPage },
      })
      .then((response) => {
        setProducts(response.data.products);
        setTotalPages(response.data.last_page);
        setTotalItems(response.data.total);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });

  };

  const viewProductStats = () => {
    axios
      .get(`${baseUrl}/admin/product/stats`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminAuthToken}`,
          },
      })
      .then((response) => setStats(response.data))
      .catch((error) => console.log(error));
  };

  const handleApprove = (productId) => {
    const formdata = new FormData();
    formdata.append("product_id", productId);
    axios
      .post(`${baseUrl}/admin/product/approveProduct`, formdata, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then(() => { viewAllProduct(); toast.success("Product Approved Successfully"); viewProductStats(); })
      .catch(() => toast.error("Something went wrong"));
  };

  const handleReject = (productId) => {
    const formdata = new FormData();
    formdata.append("product_id", productId);
    axios
      .post(`${baseUrl}/admin/product/rejectProduct`, formdata, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then(() => { viewAllProduct(); toast.error("Product Rejected"); viewProductStats(); })
      .catch(() => toast.error("Something went wrong"));
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setActiveImageIndex(0);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedProduct(null);
    setActiveImageIndex(0);
  };

  // currentPage jab bhi change ho, backend se woh page mangwao
  useEffect(() => {
    viewAllProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    viewProductStats();
  }, []);

  // Search sirf isi page pe load hue products mein hoti hai (frontend filter)
  const filteredProducts = products.filter((p) =>
    [p.product_name, p.seller?.store_name, String(p.id)]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const selectedImages = selectedProduct?.images || [];
  const activeImagePath = selectedImages[activeImageIndex]?.image_path;

  // Max 5 page-number buttons visible
  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    const pages = [];
    for (let page = startPage; page <= endPage; page++) pages.push(page);
    return pages;
  };

  return (
    <AdminMasterLayout>
      <ToastContainer />
      <div className={`pm-card-shell ${isDark ? "dark-mode" : ""}`}>

        {/* Stat Cards */}
        <div className="pm-summary-grid">
          <div className="pm-summary-card">
            <div>
              <span className="pm-label">Total Products</span>
              <h3>{stats?.total_products ?? "0"}</h3>
              <p className="pm-muted mb-0">Across all categories</p>
            </div>
            <div className="pm-card-icon"><FiPackage size={22} /></div>
          </div>
          <div className="pm-summary-card">
            <div>
              <span className="pm-label">Pending Approval</span>
              <h3>{stats?.pending_products ?? "0"}</h3>
              <p className="pm-muted mb-0">Waiting moderation</p>
            </div>
            <div className="pm-card-icon"><FiClock size={22} /></div>
          </div>
          <div className="pm-summary-card">
            <div>
              <span className="pm-label">Active Products</span>
              <h3>{stats?.active_products ?? "0"}</h3>
              <p className="pm-muted mb-0">Currently approved</p>
            </div>
            <div className="pm-card-icon"><FiCheckCircle size={22} /></div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="pm-toolbar">
          <InputGroup className="pm-search-group">
            <InputGroup.Text className="pm-search-icon"><FiSearch size={14} /></InputGroup.Text>
            <Form.Control
              className="pm-search-input"
              placeholder="Search by product, shop or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>

        {/* Table */}
        <div className="pm-layout">
          <div className="pm-table-wrapper">
            <Table className="pm-table" responsive>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>ID</th>
                  <th>Shop</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <span className="pm-loading-text pm-muted">Loading...</span>
                    </td>
                  </tr>
                )}
                {!loading && filteredProducts.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="pm-cell-product">
                        {p.images?.[0]?.image_path ? (
                          <img
                            src={`${baseUrlForFiles}/${p.images[0].image_path}`}
                            alt={p.product_name}
                            className="pm-product-img"
                          />
                        ) : (
                          <div className="pm-thumb-placeholder" />
                        )}
                        <p className="pm-name mb-0">{p.product_name}</p>
                      </div>
                    </td>
                    <td>{p.id}</td>
                    <td>{p.seller?.store_name || "—"}</td>
                    <td>{p.category?.category_name}</td>
                    <td>{p.price}</td>
                    <td>
                      <span className={`pm-status pm-status--${
                        p.status === "Approved" ? "approved"
                        : p.status === "Pending" ? "pending"
                        : p.status === "Rejected" ? "rejected"
                        : "inactive"
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td>
                      <div className="pm-actions">
                        <Button
                          className="pm-btn-view-product"
                          title="View Product"
                          onClick={() => handleViewProduct(p)}
                        >
                          <FiEye size={13} /> View Product
                        </Button>
                        <Button variant="success" size="sm" className="pm-action-btn" title="Approve Product" onClick={() => handleApprove(p.id)}>
                          <FiCheck size={13} />
                        </Button>
                        <Button variant="danger" size="sm" className="pm-action-btn" title="Reject Product" onClick={() => handleReject(p.id)}>
                          <FiX size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredProducts.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="pm-empty-icon">📦</div>
                      <p className="pm-muted mt-2">No products found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* Pagination controls */}
            {!loading && filteredProducts.length > 0 && totalPages > 1 && (
              <div className="pm-pagination-bar">
                <span className="pm-pagination-info">
                  Showing {(currentPage - 1) * itemsPerPage + 1}-
                  {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                </span>
                <div className="pm-pagination-controls">
                  <button
                    className="pm-page-btn pm-page-btn--nav"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <FiChevronLeft size={14} />
                  </button>
                  {getVisiblePageNumbers().map((page) => (
                    <button
                      key={page}
                      className={`pm-page-btn ${page === currentPage ? "pm-page-btn--active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="pm-page-btn pm-page-btn--nav"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <FiChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* View Product Modal */}
      {showViewModal && selectedProduct && (
        <div className="pm-view-modal-overlay" onClick={closeViewModal}>
          <div
            className={`pm-view-modal-content ${isDark ? "dark-mode" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pm-view-modal-header">
              <h4>Product Details</h4>
              <button className="pm-view-modal-close" onClick={closeViewModal}>
                <FiX size={18} />
              </button>
            </div>

            <div className="pm-view-modal-body">
              {/* Main Image — boxed + contain, so the full image always shows, never cropped */}
              <div className="pm-view-image-box">
                {activeImagePath ? (
                  <img
                    src={`${baseUrlForFiles}/${activeImagePath}`}
                    alt={selectedProduct.product_name}
                    className="pm-view-main-image"
                  />
                ) : (
                  <FiPackage size={40} color="#fb7f4f" />
                )}
              </div>

              {/* Thumbnails, if more than one image */}
              {selectedImages.length > 1 && (
                <div className="pm-view-thumbs">
                  {selectedImages.map((img, index) => (
                    <div
                      key={img.id || index}
                      className={`pm-view-thumb-box ${index === activeImageIndex ? "active" : ""}`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img
                        src={`${baseUrlForFiles}/${img.image_path}`}
                        alt={`${selectedProduct.product_name}-${index}`}
                        className="pm-view-thumb"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Name + Price */}
              <div className="pm-view-title-row">
                <h3 className="pm-view-product-name">{selectedProduct.product_name}</h3>
                <span className="pm-view-price">Rs {selectedProduct.price}</span>
              </div>
              <p className="pm-view-shop-line">
                {selectedProduct.seller?.store_name || "—"}
              </p>

              {/* Details grid */}
              <div className="pm-view-details-grid">
                <div className="pm-view-detail-item">
                  <span className="pm-view-detail-label">Product ID</span>
                  <span className="pm-view-detail-value">{selectedProduct.id}</span>
                </div>
                <div className="pm-view-detail-item">
                  <span className="pm-view-detail-label">Category</span>
                  <span className="pm-view-detail-value">
                    {selectedProduct.category?.category_name || "—"}
                  </span>
                </div>
                <div className="pm-view-detail-item">
                  <span className="pm-view-detail-label">Sub Category</span>
                  <span className="pm-view-detail-value">
                    {selectedProduct.subcategory?.subcategoryname || "—"}
                  </span>
                </div>
                <div className="pm-view-detail-item">
                  <span className="pm-view-detail-label">Stock</span>
                  <span className="pm-view-detail-value">
                    {selectedProduct.stock_quantity ?? "—"}
                  </span>
                </div>
                <div className="pm-view-detail-item">
                  <span className="pm-view-detail-label">Status</span>
                  <span
                    className={`pm-status pm-status--${
                      selectedProduct.status === "Approved" ? "approved"
                      : selectedProduct.status === "Pending" ? "pending"
                      : selectedProduct.status === "Rejected" ? "rejected"
                      : "inactive"
                    }`}
                  >
                    {selectedProduct.status}
                  </span>
                </div>
              </div>

              {/* Colors */}
              {selectedProduct.variant?.colors && (
                <div className="pm-view-section">
                  <span className="pm-view-section-label">Colors</span>
                  <div className="pm-view-colors-row">
                    {selectedProduct.variant.colors
                      .split(",")
                      .filter((c) => c.trim())
                      .map((c, i) => (
                        <span
                          key={i}
                          className="pm-view-color-dot"
                          style={{ backgroundColor: c.trim() }}
                          title={c.trim()}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Sizes */}
              {selectedProduct.variant?.sizes && (
                <div className="pm-view-section">
                  <span className="pm-view-section-label">Sizes</span>
                  <div className="pm-view-sizes-row">
                    {selectedProduct.variant.sizes
                      .split(",")
                      .filter((s) => s.trim())
                      .map((s, i) => (
                        <span key={i} className="pm-view-size-badge">{s.trim()}</span>
                      ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="pm-view-section">
                <span className="pm-view-section-label">Description</span>
                <p className="pm-view-desc-text">
                  {selectedProduct.description || "No description provided."}
                </p>
              </div>
            </div>

            <div className="pm-view-modal-footer">
              <button
                className="pm-view-btn-reject"
                onClick={() => {
                  handleReject(selectedProduct.id);
                  closeViewModal();
                }}
              >
                <FiX size={14} /> Reject
              </button>
              <button
                className="pm-view-btn-approve"
                onClick={() => {
                  handleApprove(selectedProduct.id);
                  closeViewModal();
                }}
              >
                <FiCheck size={14} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminMasterLayout>
  );
};

export default ProductManagement;